import { Card, Form, Input, Switch, Select, Button, Spin, message, Divider, Tag, Table, Empty } from "antd";
import { useAuth } from "~/store";
import { useOrganization, useUpdateOrganization } from "~/services/organization";
import { useGetBusinessAddresses } from "~/services/business-addresses";
import { SubscriptionTier } from "~/utils/constants";
import { useEffect } from "react";
import { Link } from "react-router";
import type { TBusinessAddress } from "~/types/business-address";

export const OrganizationSettings = () => {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const { data: org, isLoading } = useOrganization(orgId || "");
  const { data: addresses = [], isLoading: loadingAddresses } = useGetBusinessAddresses(true);
  const updateOrg = useUpdateOrganization();
  const [form] = Form.useForm();

  useEffect(() => {
    if (org) {
      form.setFieldsValue(org);
    }
  }, [org, form]);

  if (isLoading) return <Spin className="flex justify-center mt-20" />;
  if (!org) return <div className="p-6">No organization found</div>;

  const handleSave = async (values: any) => {
    try {
      await updateOrg.mutateAsync({ id: org.id, body: values });
      message.success("Organization updated");
    } catch {
      message.error("Failed to update organization");
    }
  };

  const addressColumns = [
    {
      title: "Location",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: TBusinessAddress) => (
        <div>
          <span className="font-medium">{name}</span>
          <Tag className="ml-2" color={record.addressType === "warehouse" ? "blue" : record.addressType === "office" ? "green" : "orange"}>
            {record.addressType}
          </Tag>
        </div>
      ),
    },
    {
      title: "Address",
      key: "address",
      render: (_: any, record: TBusinessAddress) => (
        <span className="text-sm text-gray-600">
          {record.address}{record.address2 ? `, ${record.address2}` : ""}, {record.city}, {record.state} {record.zip}
        </span>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_: any, record: TBusinessAddress) => (
        <div className="text-sm">
          {record.contactPerson && <div>{record.contactPerson}</div>}
          {record.phone && <div className="text-gray-500">{record.phone}</div>}
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-xl font-bold">Organization Settings</h1>
        {org.isPlatformOwner && (
          <Tag color="black" className="text-xs font-semibold">Platform Owner</Tag>
        )}
      </div>

      <Card className="mb-6">
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Divider orientation="left" className="!mt-0">General</Divider>

          <Form.Item name="name" label="Organization Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="slug" label="Slug (URL identifier)">
              <Input disabled />
            </Form.Item>
            <Form.Item name="timezone" label="Timezone">
              <Select
                options={[
                  { value: "America/New_York", label: "Eastern (ET)" },
                  { value: "America/Chicago", label: "Central (CT)" },
                  { value: "America/Denver", label: "Mountain (MT)" },
                  { value: "America/Los_Angeles", label: "Pacific (PT)" },
                ]}
              />
            </Form.Item>
          </div>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Brief description of your organization..." />
          </Form.Item>

          <Divider orientation="left">Contact Information</Divider>

          <div className="grid grid-cols-3 gap-4">
            <Form.Item name="phone" label="Phone">
              <Input placeholder="(555) 123-4567" />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input placeholder="info@company.com" />
            </Form.Item>
            <Form.Item name="website" label="Website">
              <Input placeholder="https://company.com" />
            </Form.Item>
          </div>

          <Divider orientation="left">Plan & Network</Divider>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="subscriptionTier" label="Plan">
              <Select
                disabled={!org.isPlatformOwner}
                options={Object.values(SubscriptionTier).map((t) => ({
                  value: t,
                  label: t.charAt(0).toUpperCase() + t.slice(1),
                }))}
              />
            </Form.Item>
            <Form.Item name="networkEnabled" label="Enable Network" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>

          <p className="text-xs text-gray-500 mb-6">
            When enabled, your organization can share shuttle routes and receive job referrals from other art handling companies on the platform.
          </p>

          <Button type="primary" htmlType="submit" loading={updateOrg.isPending}
            className="bg-black hover:bg-gray-800 border-black">
            Save Changes
          </Button>
        </Form>
      </Card>

      <Card
        title="Business Locations"
        extra={
          <Link to="/operations/backend-setup?tab=business-addresses" className="text-xs text-gray-500 hover:text-black">
            Manage in Backend Setup
          </Link>
        }
      >
        {loadingAddresses ? (
          <Spin className="flex justify-center py-8" />
        ) : addresses.length === 0 ? (
          <Empty
            description={
              <span>
                No business addresses configured.{" "}
                <Link to="/operations/backend-setup?tab=business-addresses" className="text-blue-600">
                  Add one in Backend Setup
                </Link>
              </span>
            }
          />
        ) : (
          <Table
            dataSource={addresses}
            columns={addressColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        )}
      </Card>
    </div>
  );
};
